import { Fragment, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { ColumnVisibilityDropdown } from './ColumnVisibilityDropdown';
import { ColumnFilterPopover } from './ColumnFilterPopover';
import { filterChipText, matchesFilter, type ColFilter } from './columnFilter';
import type { TableController } from './useServerDataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Spinner } from '@/components/common/Spinner';
import './DataGrid.css';

export interface DataGridColumn<T> {
  key: string;
  // Header text. `header` is an accepted alias (DataTable compat); `title ?? header`.
  title?: ReactNode;
  header?: ReactNode;
  // Cell renderer; defaults to String(row[key]).
  cell?: (row: T) => ReactNode;
  // Value used for client-side sorting + Excel export (defaults to row[key]).
  value?: (row: T) => string | number | null | undefined;
  sortable?: boolean; // default true
  defaultVisible?: boolean; // default true
  hideable?: boolean; // appears in the eye menu; default true
  // Gets a funnel filter + right-click menu when columnFilters is on; default true.
  // In controller (server) mode, a per-column search input shows only when `true`.
  filterable?: boolean;
  // Server column-search param name (defaults to `key`) + input placeholder.
  filterKey?: string;
  filterPlaceholder?: string;
  className?: string; // td/th class (e.g. "text-end")
  headerClassName?: string;
}

// Header text, resolving the `header` alias.
function titleOf<T>(c: DataGridColumn<T>): ReactNode {
  return c.title ?? c.header;
}

interface DataGridProps<T> {
  columns: DataGridColumn<T>[];
  // Optional when a `controller` is supplied (it provides the rows).
  data?: T[];
  rowKey: (row: T) => string;
  pageSize?: number;
  pageSizeOptions?: number[];
  // The "eye" column-visibility dropdown — shown by default, like the legacy.
  columnVisibilityToggle?: boolean;
  // Per-column funnel filters + right-click cell menu + active-filter chip bar.
  // (Risk Dashboard only — other tables use columnSearch.)
  columnFilters?: boolean;
  // Per-column inline filter inputs under the header (legacy finkit-dt-search-row).
  columnSearch?: boolean;
  // When provided, columnSearch inputs become controlled + server-driven: values read
  // from here and changes call onColumnSearchChange (no client-side filtering applied).
  columnSearchValues?: Record<string, string>;
  onColumnSearchChange?: (key: string, value: string) => void;
  // Standard DataTable global search box (top-right).
  searchable?: boolean;
  // Client-side only: when false, drop the length selector, the "Showing x to y of z"
  // info line and the pager, and render every row. For small embedded tables (e.g. the
  // Client Assets breakdown panels) where paging chrome is just noise. Default true.
  paginated?: boolean;
  // Server-side mode: the parent (useServerDataTable) owns paging; DataGrid renders
  // `data` as the current page as-is and drives paging via these callbacks.
  server?: {
    pageIndex: number;
    pageSize: number;
    pageCount: number;
    totalCount: number;
    onPageChange: (p: number) => void;
    onPageSizeChange: (n: number) => void;
  };
  // A <tfoot> rendered as a top-group row (the caller supplies the <tr>); used for
  // the NOP / LP totals row that shows at the top of the table.
  footerTop?: ReactNode;
  // Like footerTop, but keyed by column so DataGrid renders the totals <tr> itself
  // over the currently-visible columns — a hidden column drops its totals cell too,
  // keeping the row aligned when the column-visibility toggle is used. Keys missing
  // from the map render `footerCellDefault` (default "-").
  footerCells?: Record<string, ReactNode>;
  footerCellDefault?: ReactNode;
  footerRowClassName?: string;
  // Server-mode convenience: pass a useServerDataTable() controller and DataGrid
  // derives `data`, server paging, server SORT (clickable headers), per-column
  // server search (columns with `filterable: true`), the loading scrim + initial
  // spinner, and an error/retry state — no need to also pass `data`/`server`.
  controller?: TableController<T>;
  rowActions?: (row: T) => ReactNode; // adds a trailing Actions column
  toolbarStart?: ReactNode; // table action (e.g. Add button)
  toolbarEnd?: ReactNode; // right-aligned toolbar slot (before the eye dropdown)
  // Inline create row (legacy finkitRowEdit "add"): per-column cell content keyed by
  // column key, rendered as the first body row; actions go in the Actions column.
  createRow?: Record<string, ReactNode>;
  createRowActions?: ReactNode;
  includeExcel?: boolean;
  excelFileName?: string;
  expandable?: (row: T) => ReactNode; // renders a child row; adds a leading expand column
  // Controlled single-open expansion: when provided, the caret reflects this set
  // and clicks call onExpandToggle instead of mutating internal state. Lets the
  // parent enforce single-open + run guards (e.g. discard-unsaved-changes).
  expandedKeys?: Set<string>;
  onExpandToggle?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  // When set, the whole body row becomes clickable (legacy GoToTransactions-style
  // row navigation). Clicks originating inside the Actions column are ignored so
  // row buttons keep working.
  onRowClick?: (row: T) => void;
  emptyMessage?: ReactNode;
  tableClassName?: string;
  // Wrap the whole grid in a themed card (card custom-card > card-body p-0), like the
  // legacy datatable wrapper. Off by default (pages that already supply their own card).
  card?: boolean;
  // When card is on, renders a centered card-header title (legacy titled-table layout).
  title?: ReactNode;
  // Stretch the card to its column height (legacy equal-height grid rows).
  fillHeight?: boolean;
  // Fires when the page-length (entries) <select> gains/loses focus. Lets a live
  // page defer table redraws while the user has the length dropdown open (legacy
  // NopSectionTable._isDropdownOpen deferral).
  onLengthFocusChange?: (focused: boolean) => void;
  // Server-side only: overlay a loading scrim while a new page/size is fetched.
  // DataGrid also auto-detects fetches triggered by its own pager/length controls
  // (set on change, cleared when the new `data` arrives — relies on the parent's
  // keepPreviousData), so passing this explicitly is optional.
  loading?: boolean;
}

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

const DEFAULT_SIZES = [5, 10, 25, 50];

function cmp(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

export function DataGrid<T>({
  columns,
  data: dataProp,
  rowKey,
  pageSize = 5,
  pageSizeOptions = DEFAULT_SIZES,
  columnVisibilityToggle = true,
  columnFilters = false,
  columnSearch = false,
  columnSearchValues,
  onColumnSearchChange,
  searchable = false,
  paginated = true,
  server: serverProp,
  controller,
  footerTop,
  footerCells,
  footerCellDefault = '-',
  footerRowClassName = 'datatable-footer-row',
  rowActions,
  toolbarStart,
  toolbarEnd,
  createRow,
  createRowActions,
  includeExcel = false,
  excelFileName = 'export',
  expandable,
  expandedKeys,
  onExpandToggle,
  rowClassName,
  onRowClick,
  emptyMessage = 'No data available',
  tableClassName = 'table dataTable table-striped table-hover w-100 mb-0 text-nowrap',
  card = false,
  title,
  fillHeight = false,
  onLengthFocusChange,
  loading = false,
}: DataGridProps<T>) {
  // A controller (useServerDataTable) supplies the data + server-paging wiring;
  // otherwise fall back to the explicit `data` / `server` props.
  const data = controller ? controller.rows : (dataProp ?? []);
  const server = controller
    ? {
        pageIndex: controller.pageIndex,
        pageSize: controller.pageSize,
        pageCount: controller.pageCount,
        totalCount: controller.totalCount,
        onPageChange: controller.setPageIndex,
        onPageSizeChange: controller.setPageSize,
      }
    : serverProp;
  // Explicit server mode WITHOUT a controller (client sort/indicators stay disabled).
  const plainServer = !controller && !!serverProp;

  const [sort, setSort] = useState<SortState>(null);
  const [size, setSize] = useState(pageSize);
  const [page, setPage] = useState(0);
  // Server-side page/size fetch feedback: set when the pager/length requests new
  // data, cleared when the new page `data` arrives (keepPreviousData keeps the ref
  // stable during the fetch). Mirrors the host DataTable scrim.
  const [pending, setPending] = useState(false);
  useEffect(() => {
    setPending(false);
  }, [data]);
  const busy = loading || pending || (controller?.isFetching ?? false);
  // Active sort to render an indicator for: the controller's server sort, else the
  // client sort (never in plain-server or funnel-filter modes).
  const activeSort: SortState = controller ? controller.sort : (plainServer || columnFilters ? null : sort);
  // Per-column server search row (controller mode): shown if any column opts in.
  const controllerSearch = !!controller && columns.some((c) => c.filterable);
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(columns.filter((c) => c.defaultVisible === false).map((c) => c.key)),
  );
  const [search, setSearch] = useState('');
  const [colSearch, setColSearch] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<Record<string, ColFilter>>({});
  const [openFilter, setOpenFilter] = useState<{ key: string; anchor: DOMRect } | null>(null);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; key: string; value: string } | null>(null);

  const valueOf = (col: DataGridColumn<T>, row: T): string | number | null | undefined =>
    col.value ? col.value(row) : (row as Record<string, unknown>)[col.key] as string | number | null | undefined;
  const cellStr = (col: DataGridColumn<T>, row: T): string => {
    const v = valueOf(col, row);
    return v == null ? '' : String(v);
  };
  const isFilterable = (col: DataGridColumn<T>) => columnFilters && col.filterable !== false;

  const filtered = useMemo(() => {
    const active = Object.entries(filters);
    if (!active.length) return data;
    return data.filter((row) =>
      active.every(([key, f]) => {
        const col = columns.find((c) => c.key === key);
        return col ? matchesFilter(cellStr(col, row), f) : true;
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filters, columns]);

  const colSearched = useMemo(() => {
    // Server-driven column search: the parent filters, don't filter client-side.
    if (onColumnSearchChange) return filtered;
    const active = Object.entries(colSearch).filter(([, v]) => v.trim());
    if (!active.length) return filtered;
    return filtered.filter((row) =>
      active.every(([key, v]) => {
        const col = columns.find((c) => c.key === key);
        return col ? cellStr(col, row).toLowerCase().includes(v.trim().toLowerCase()) : true;
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, colSearch, columns]);

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return colSearched;
    return colSearched.filter((row) => columns.some((c) => cellStr(c, row).toLowerCase().includes(q)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colSearched, search, columns]);

  const sorted = useMemo(() => {
    if (!sort) return searched;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return searched;
    const arr = [...searched].sort((a, b) => cmp(valueOf(col, a), valueOf(col, b)));
    if (sort.dir === 'desc') arr.reverse();
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, sort, columns]);

  const distinctValues = (key: string): string[] => {
    const col = columns.find((c) => c.key === key);
    if (!col) return [];
    const set = new Set<string>();
    data.forEach((row) => set.add(cellStr(col, row)));
    return [...set].sort((a, b) => cmp(a, b));
  };

  const setColFilter = (key: string, f: ColFilter | null) =>
    setFilters((prev) => {
      const next = { ...prev };
      if (f) next[key] = f;
      else delete next[key];
      return next;
    });

  // Server mode: paging/total come from the parent; `data` is already the current page.
  const effSize = server ? server.pageSize : size;
  const total = server ? server.totalCount : sorted.length;
  const pageCount = Math.max(1, server ? server.pageCount : Math.ceil(total / effSize));
  const safePage = server ? server.pageIndex : Math.min(page, pageCount - 1);
  const start = safePage * effSize;
  // Client-side unpaginated: show everything; server mode is always paged.
  const rows = server ? data : paginated ? sorted.slice(start, start + effSize) : sorted;

  const visibleCols = columns.filter((c) => !hidden.has(c.key));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const colSpan = visibleCols.length + (expandable ? 1 : 0) + (rowActions ? 1 : 0);

  function toggleSort(col: DataGridColumn<T>) {
    if (col.sortable === false) return;
    setSort((s) =>
      !s || s.key !== col.key ? { key: col.key, dir: 'asc' } : s.dir === 'asc' ? { key: col.key, dir: 'desc' } : null,
    );
  }

  function toggleExpand(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function exportExcel() {
    const header = visibleCols.map((c) => `"${String(titleOf(c) ?? '').replace(/"/g, '""')}"`).join(',');
    const lines = sorted.map((row) =>
      visibleCols
        .map((c) => {
          const v = valueOf(c, row);
          return `"${(v == null ? '' : String(v)).replace(/"/g, '""')}"`;
        })
        .join(','),
    );
    const csv = [header, ...lines].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${excelFileName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Legacy DataTables "full_numbers" style: first ‹ last with a window around the
  // current page and an ellipsis to the first/last page.
  const pages = useMemo<(number | 'ellipsis')[]>(() => {
    const last = pageCount - 1;
    const DELTA = 2;
    let lo = safePage - DELTA;
    let hi = safePage + DELTA;
    if (lo < 0) { hi += -lo; lo = 0; }
    if (hi > last) { lo -= hi - last; hi = last; }
    lo = Math.max(0, lo);
    hi = Math.min(last, hi);
    const out: (number | 'ellipsis')[] = [];
    if (lo > 0) { out.push(0); if (lo > 1) out.push('ellipsis'); }
    for (let i = lo; i <= hi; i++) out.push(i);
    if (hi < last) { if (hi < last - 1) out.push('ellipsis'); out.push(last); }
    return out;
  }, [safePage, pageCount]);

  const goTo = (p: number) => {
    // No-op for the current page or an out-of-range target (e.g. clicking the
    // active page number, or Next/Prev at the ends). In server mode this also
    // avoids a stuck scrim: re-requesting the same page doesn't refetch, so the
    // `data`-change effect that clears `pending` would never fire.
    if (p === safePage || p < 0 || p >= pageCount) return;
    if (server) {
      setPending(true);
      server.onPageChange(p);
    } else {
      setPage(p);
    }
  };

  // Hide the whole top toolbar when there is nothing to show (unpaginated table with
  // no search / excel / eye / action), so a bare breakdown table has no empty chrome.
  const showToolbar = paginated || Boolean(toolbarStart) || Boolean(toolbarEnd) || searchable || includeExcel || columnVisibilityToggle;

  const grid = (
    <div className="finkitDatatable">
      {/* Top toolbar: length + table action + eye + excel */}
      {showToolbar && (
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 py-1">
        <div className="d-flex align-items-center gap-2">
          {paginated && (
          <label className="d-inline-flex align-items-center gap-1 mb-0 small text-nowrap">
            Show
            <select
              className="form-select form-select-sm d-inline-block w-auto"
              value={effSize}
              onFocus={() => onLengthFocusChange?.(true)}
              onBlur={() => onLengthFocusChange?.(false)}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (server) { setPending(true); server.onPageSizeChange(n); }
                else { setSize(n); setPage(0); }
                onLengthFocusChange?.(false);
              }}
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            entries
          </label>
          )}
        </div>
        <div className="d-flex align-items-center gap-2 column-toggle-wrapper">
          {toolbarStart}
          {toolbarEnd}
          {searchable && (
            <label className="d-inline-flex align-items-center gap-1 mb-0 small text-nowrap">
              Search:
              <input
                type="search"
                className="form-control form-control-sm d-inline-block w-auto dataTable-search-input"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </label>
          )}
          {includeExcel && (
            <button type="button" className="btn btn-sm btn-outline-primary" title="Export to Excel" onClick={exportExcel}>
              <i className="fa fa-download" />
            </button>
          )}
          {columnVisibilityToggle && (
            <ColumnVisibilityDropdown
              columns={columns.filter((c) => c.hideable !== false).map((c) => ({ key: c.key, label: titleOf(c) }))}
              hidden={hidden}
              onReset={() => setHidden(new Set())}
              onToggle={(key) =>
                setHidden((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                })
              }
            />
          )}
        </div>
      </div>
      )}

      {columnFilters && Object.keys(filters).length > 0 && (
        <div className="rd-table-filter-chips d-flex flex-wrap align-items-center gap-1 px-2 pb-1">
          <span className="rd-chips-label fs-11 text-muted text-uppercase fw-semibold me-1">Active filters</span>
          {Object.entries(filters).map(([key, f]) => {
            const col = columns.find((c) => c.key === key);
            const label = col ? titleOf(col) : undefined;
            return (
              <button key={key} type="button" className="rd-scope-chip btn btn-sm" onClick={() => setColFilter(key, null)}>
                {filterChipText(typeof label === 'string' ? label : key, f)} <span aria-hidden>×</span>
              </button>
            );
          })}
          <button type="button" className="btn btn-link btn-sm py-0" onClick={() => setFilters({})}>
            Clear all
          </button>
        </div>
      )}

      {controller?.isError ? (
        <ErrorState onRetry={controller.refetch} />
      ) : (
      <div className="position-relative">
        {busy && (
          // Overlay lives OUTSIDE the horizontal scroll area so it stays centered in
          // the visible viewport even when the table is scrolled sideways.
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ position: 'absolute', inset: 0, zIndex: 5, background: 'rgba(127, 127, 127, 0.18)' }}
          >
            <span className="spinner-border text-primary" role="status" aria-label="Loading" />
          </div>
        )}
        <div className="table-responsive finkit-table-scroll">
        <table
          className={tableClassName}
          style={busy ? { opacity: 0.5, pointerEvents: 'none', transition: 'opacity .1s' } : undefined}
        >
          <thead>
            <tr>
              {expandable && <th style={{ width: 32 }} />}
              {visibleCols.map((c) => {
                const sortableCol = c.sortable !== false && !columnFilters;
                // Clickable to sort: client mode or controller(server) mode — but not
                // plain-server (no controller) where there's no sort handler.
                const clickable = sortableCol && (controller ? true : !plainServer);
                return (
                <th
                  key={c.key}
                  className={c.headerClassName ?? c.className}
                  style={{ cursor: clickable ? 'pointer' : 'default' }}
                  onClick={clickable ? () => (controller ? controller.toggleSort(c.key) : toggleSort(c)) : undefined}
                >
                  {titleOf(c)}
                  {sortableCol && activeSort?.key === c.key && (
                    <i className={`ms-1 fe ${activeSort.dir === 'asc' ? 'fe-chevron-up' : 'fe-chevron-down'}`} />
                  )}
                  {isFilterable(c) && (
                    <button
                      type="button"
                      className={`rd-col-filter-btn${filters[c.key] ? ' on' : ''}`}
                      title="Filter & sort"
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setOpenFilter((o) => (o?.key === c.key ? null : { key: c.key, anchor: rect }));
                      }}
                    >
                      <i className="fe fe-filter" />
                      {sort?.key === c.key && (
                        <i className={`ms-1 fe ${sort.dir === 'asc' ? 'fe-arrow-up' : 'fe-arrow-down'}`} />
                      )}
                    </button>
                  )}
                </th>
                );
              })}
              {rowActions && <th>Actions</th>}
            </tr>
            {(columnSearch || controllerSearch) && (
              <tr className="finkit-dt-search-row">
                {expandable && <th />}
                {visibleCols.map((c) => (
                  <th key={c.key}>
                    {controller ? (
                      // Controller (server) mode: only columns that opt in with
                      // `filterable: true` get a search input.
                      c.filterable ? (
                        <input
                          type="search"
                          className="form-control form-control-sm"
                          placeholder={typeof c.filterPlaceholder === 'string' ? c.filterPlaceholder : undefined}
                          value={controller.columnFilters[c.filterKey ?? c.key] ?? ''}
                          onChange={(e) => controller.setColumnFilter(c.filterKey ?? c.key, e.target.value)}
                        />
                      ) : null
                    ) : c.filterable === false ? null : (
                      <input
                        type="search"
                        className="form-control form-control-sm"
                        value={onColumnSearchChange ? (columnSearchValues?.[c.key] ?? '') : (colSearch[c.key] ?? '')}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (onColumnSearchChange) {
                            onColumnSearchChange(c.key, v);
                          } else {
                            setColSearch((prev) => ({ ...prev, [c.key]: v }));
                            setPage(0);
                          }
                        }}
                      />
                    )}
                  </th>
                ))}
                {rowActions && <th />}
              </tr>
            )}
          </thead>
          {footerTop && <tfoot className="dt-top-totals">{footerTop}</tfoot>}
          {!footerTop && footerCells && (
            <tfoot className="dt-top-totals">
              <tr className={footerRowClassName}>
                {expandable && <td />}
                {visibleCols.map((c) => (
                  <td key={c.key} className={c.className}>
                    {c.key in footerCells ? footerCells[c.key] : footerCellDefault}
                  </td>
                ))}
                {rowActions && <td />}
              </tr>
            </tfoot>
          )}
          <tbody>
            {createRow && (
              <tr className="finkit-create-row">
                {expandable && <td />}
                {visibleCols.map((c) => (
                  <td key={c.key} className={c.className}>
                    {createRow[c.key]}
                  </td>
                ))}
                {rowActions && <td>{createRowActions}</td>}
              </tr>
            )}
            {controller?.isLoading ? (
              <tr>
                <td colSpan={colSpan}>
                  <Spinner />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="text-center text-muted py-4">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const key = rowKey(row);
                const controlledExpand = expandedKeys !== undefined;
                const isOpen = controlledExpand ? expandedKeys.has(key) : expanded.has(key);
                return (
                  <Fragment key={key}>
                    <tr
                      className={[rowClassName?.(row), onRowClick ? 'cursor-pointer' : undefined].filter(Boolean).join(' ') || undefined}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                      {expandable && (
                        <td>
                          <button type="button" className="rd-expand-btn" title="Expand" onClick={() => (controlledExpand ? onExpandToggle?.(row) : toggleExpand(key))}>
                            <i className={`fe ${isOpen ? 'fe-chevron-down' : 'fe-chevron-right'}`} />
                          </button>
                        </td>
                      )}
                      {visibleCols.map((c) => (
                        <td
                          key={c.key}
                          className={c.className}
                          onContextMenu={
                            isFilterable(c)
                              ? (e) => {
                                  e.preventDefault();
                                  setCtxMenu({
                                    x: Math.min(e.clientX, window.innerWidth - 180),
                                    y: Math.min(e.clientY, window.innerHeight - 140),
                                    key: c.key,
                                    value: cellStr(c, row),
                                  });
                                }
                              : undefined
                          }
                        >
                          {c.cell ? c.cell(row) : String((row as Record<string, unknown>)[c.key] ?? '')}
                        </td>
                      ))}
                      {rowActions && (
                        <td onClick={onRowClick ? (e) => e.stopPropagation() : undefined}>{rowActions(row)}</td>
                      )}
                    </tr>
                    {expandable && isOpen && (
                      <tr className="rd-child-detail-row">
                        <td colSpan={colSpan}>{expandable(row)}</td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
      )}

      {/* Bottom: info + pagination */}
      {paginated && (
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 py-1">
        <div className="dt-info small text-muted">
          {total === 0 ? 'Showing 0 to 0 of 0 entries' : `Showing ${start + 1} to ${Math.min(start + effSize, total)} of ${total} entries`}
        </div>
        {/* Lock the pager while a fetch is in flight: the next page can't be
            activated until the current page's data has actually loaded. */}
        <nav className="dt-paging" style={busy ? { pointerEvents: 'none', opacity: 0.55 } : undefined}>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item${safePage === 0 ? ' disabled' : ''}`}>
              <button type="button" className="page-link" title="First" onClick={() => goTo(0)}>«</button>
            </li>
            <li className={`page-item${safePage === 0 ? ' disabled' : ''}`}>
              <button type="button" className="page-link" title="Previous" onClick={() => goTo(safePage - 1)}>‹</button>
            </li>
            {pages.map((p, i) =>
              p === 'ellipsis' ? (
                <li key={`e${i}`} className="page-item disabled">
                  <span className="page-link">…</span>
                </li>
              ) : (
                <li key={p} className={`page-item${p === safePage ? ' active' : ''}`}>
                  <button type="button" className="page-link" onClick={() => goTo(p)}>{p + 1}</button>
                </li>
              ),
            )}
            <li className={`page-item${safePage >= pageCount - 1 ? ' disabled' : ''}`}>
              <button type="button" className="page-link" title="Next" onClick={() => goTo(safePage + 1)}>›</button>
            </li>
            <li className={`page-item${safePage >= pageCount - 1 ? ' disabled' : ''}`}>
              <button type="button" className="page-link" title="Last" onClick={() => goTo(pageCount - 1)}>»</button>
            </li>
          </ul>
        </nav>
      </div>
      )}

      {openFilter && (
        <ColumnFilterPopover
          title={(() => {
            const col = columns.find((c) => c.key === openFilter.key);
            return typeof col?.title === 'string' ? col.title : openFilter.key;
          })()}
          anchor={openFilter.anchor}
          distinctValues={distinctValues(openFilter.key)}
          current={filters[openFilter.key]}
          sortDir={sort?.key === openFilter.key ? sort.dir : null}
          onSort={(dir) => {
            setSort({ key: openFilter.key, dir });
            setOpenFilter(null);
          }}
          onApply={(f) => {
            setColFilter(openFilter.key, f);
            setPage(0);
            setOpenFilter(null);
          }}
          onClose={() => setOpenFilter(null)}
        />
      )}

      {ctxMenu &&
        createPortal(
          <>
            <div className="rd-cell-menu-backdrop" onClick={() => setCtxMenu(null)} onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }} />
            <div className="rd-cell-filter-menu" style={{ left: ctxMenu.x, top: ctxMenu.y }}>
              {([
                { op: 'equals', label: 'Filter equals' },
                { op: 'notEquals', label: 'Filter not equals' },
                { op: 'contains', label: 'Filter contains' },
              ] as const).map((o) => (
                <button
                  key={o.op}
                  type="button"
                  className="rd-cell-filter-item"
                  onClick={() => {
                    setColFilter(
                      ctxMenu.key,
                      o.op === 'contains'
                        ? { op: 'contains', text: ctxMenu.value }
                        : o.op === 'equals'
                          ? { op: 'equals', value: ctxMenu.value }
                          : { op: 'notEquals', value: ctxMenu.value },
                    );
                    setPage(0);
                    setCtxMenu(null);
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </>,
          document.body,
        )}
    </div>
  );

  if (!card) return grid;
  return (
    <div className={`card custom-card mb-0${fillHeight ? ' h-100' : ''}`}>
      {title && (
        <div className="card-header border-bottom">
          <h6 className="card-title text-center w-100 mb-0">{title}</h6>
        </div>
      )}
      <div className="card-body p-0">{grid}</div>
    </div>
  );
}
